import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, X, Clock, MapPin, Phone, Mail, User, 
  Building2, Calendar, Package, Eye, Image as ImageIcon,
  MessageSquare, FileText
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import Modal from '../../components/ui/Modal';
import { Button } from '../../components/ui/button';
import { adminApi } from '../../lib/api';
import { resolveAssetUrl } from '../../config';
import { toast } from 'sonner';

import { formatDateOnlyEST } from '../../utils/dateUtils';

const VenueApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [reviewMessage, setReviewMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [coasterDesigns, setCoasterDesigns] = useState({});

  useEffect(() => {
    fetchApplications();
    fetchCoasterDesigns();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      // Fetch all applications (no status filter) so tabs work correctly
      const response = await adminApi.get(`/admin/venue-applications`);
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchCoasterDesigns = async () => {
    try {
      const response = await adminApi.get(`/venue/designs`);
      // Build a lookup map by design ID for quick access
      const designMap = {};
      response.data.forEach(d => { designMap[d.id] = d; });
      setCoasterDesigns(designMap);
    } catch (error) {
      console.error('Failed to fetch coaster designs:', error);
    }
  };

  const handleReview = async (applicationId, approve) => {
    setProcessing(true);
    try {
      await adminApi.post(
        `/admin/venue-applications/${applicationId}/review`,
        {
          approve,
          admin_message: reviewMessage || null
        }
      );

      toast.success(approve ? 'Application approved!' : 'Application rejected');
      setSelectedApp(null);
      setReviewMessage('');
      fetchApplications();
    } catch (error) {
      console.error('Review failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to review application');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    return formatDateOnlyEST(dateString);
  };

  // Filter applications based on status
  const filteredApplications = applications.filter(app => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'pending') return app.status === 'pending';
    if (filterStatus === 'approved') return app.status === 'approved';
    if (filterStatus === 'rejected') return app.status === 'rejected';
    return true;
  });

  const pendingCount = applications.filter(a => a.status === 'pending').length;
  const approvedCount = applications.filter(a => a.status === 'approved').length;
  const rejectedCount = applications.filter(a => a.status === 'rejected').length;

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-venue border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* V5 Page Header */}
      <div className="mb-8">
        <p className="font-mono text-venue text-sm mb-1">// venue_applications</p>
        <h1 className="font-display text-3xl text-navy">Venue Partner Applications</h1>
        <p className="text-navy/50 mt-1">
          Review and approve venue applications for the Tappd Hub program • {applications.length} total
        </p>
      </div>

      {/* V5 Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilterStatus('pending')}
          className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
            filterStatus === 'pending'
              ? 'bg-amber-500 text-white shadow-lg'
              : 'bg-white text-navy/70 hover:bg-navy/5 border border-navy/10'
          }`}
          style={filterStatus === 'pending' ? {
            boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
          } : {}}
        >
          <Clock size={14} className="inline mr-1.5 -mt-0.5" />
          Pending ({pendingCount})
        </button>
        <button
          onClick={() => setFilterStatus('approved')}
          className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
            filterStatus === 'approved'
              ? 'bg-green-500 text-white shadow-lg'
              : 'bg-white text-navy/70 hover:bg-navy/5 border border-navy/10'
          }`}
          style={filterStatus === 'approved' ? {
            boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4)'
          } : {}}
        >
          <CheckCircle size={14} className="inline mr-1.5 -mt-0.5" />
          Approved ({approvedCount})
        </button>
        <button
          onClick={() => setFilterStatus('rejected')}
          className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
            filterStatus === 'rejected'
              ? 'bg-red-500 text-white'
              : 'bg-white text-navy/70 hover:bg-navy/5 border border-navy/10'
          }`}
        >
          <X size={14} className="inline mr-1.5 -mt-0.5" />
          Rejected ({rejectedCount})
        </button>
        <button
          onClick={() => setFilterStatus('all')}
          className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
            filterStatus === 'all'
              ? 'bg-business text-white shadow-lg'
              : 'bg-white text-navy/70 hover:bg-navy/5 border border-navy/10'
          }`}
          style={filterStatus === 'all' ? {
            boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
          } : {}}
        >
          All ({applications.length})
        </button>
      </div>

      {/* Empty State */}
      {filteredApplications.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-navy/5">
          <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="w-10 h-10 text-navy/20" />
          </div>
          <h3 className="font-display text-xl text-navy mb-2">
            {filterStatus === 'pending' 
              ? 'No Pending Applications' 
              : filterStatus === 'all'
              ? 'No Applications Yet'
              : `No ${filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)} Applications`
            }
          </h3>
          <p className="text-navy/50">
            {filterStatus === 'pending'
              ? 'All venue applications have been reviewed'
              : `No applications are currently marked as ${filterStatus}`
            }
          </p>
        </div>
      ) : (
        /* V5 Applications Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredApplications.map((app) => (
            <motion.div
              key={app.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all border border-navy/5 overflow-hidden cursor-pointer group"
              style={{ borderLeft: '4px solid #14b8a6' }}
              onClick={() => setSelectedApp(app)}
            >
              {/* Venue Logo */}
              <div className="h-28 bg-cream flex items-center justify-center p-4 relative">
                {app.logo_url ? (
                  <img
                    src={resolveAssetUrl(app.logo_url)}
                    alt={app.venue_name}
                    className="max-h-20 max-w-full object-contain"
                  />
                ) : (
                  <div className="w-16 h-16 bg-navy/5 rounded-xl flex items-center justify-center">
                    <ImageIcon size={28} className="text-navy/20" />
                  </div>
                )}
                {/* Status Badge */}
                <div className="absolute top-3 right-3">
                  {app.status === 'pending' && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
                      <Clock size={12} />
                      Pending
                    </span>
                  )}
                  {app.status === 'approved' && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2.5 py-1 rounded-full border border-green-200">
                      <CheckCircle size={12} />
                      Approved
                    </span>
                  )}
                  {app.status === 'rejected' && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 px-2.5 py-1 rounded-full border border-red-200">
                      <X size={12} />
                      Rejected
                    </span>
                  )}
                </div>
              </div>

              {/* Venue Info */}
              <div className="p-5">
                <h3 className="font-display text-lg text-navy mb-1">
                  {app.venue_name}
                </h3>
                <p className="text-sm text-navy/50 mb-4">{app.venue_type}</p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-navy/70">
                    <MapPin size={14} className="text-venue" />
                    <span>{app.city}, {app.state}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-navy/70">
                    <User size={14} className="text-venue" />
                    <span>{app.owner_manager_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-navy/70">
                    <Package size={14} className="text-venue" />
                    <span>{app.requested_coaster_quantity?.toLocaleString() || 0} coasters</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-navy/5">
                  <span className="text-xs text-navy/40 flex items-center gap-1">
                    <Calendar size={12} />
                    {formatDate(app.created_at)}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-venue hover:bg-venue/10"
                  >
                    <Eye size={14} className="mr-1" />
                    Review
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* V5 Application Review Modal */}
      <Modal
        isOpen={!!selectedApp}
        onClose={() => setSelectedApp(null)}
        maxWidth="max-w-4xl"
        closeLabel="Close application review"
        headingClassName="block"
        title={
          <span className="block">
            <span className="block font-mono text-venue text-xs mb-0.5">// review_application</span>
            <span className="block font-display text-2xl text-navy">{selectedApp?.venue_name}</span>
            <span className="block text-navy/50 text-sm font-normal">{selectedApp?.venue_type}</span>
          </span>
        }
      >
        {selectedApp && (
                <div>
                  {/* Logo */}
                  <div className="mb-6">
                    <label className="block text-xs font-medium text-navy/50 uppercase tracking-wide mb-2">
                      Venue Logo
                    </label>
                    {selectedApp.logo_url ? (
                      <div className="border-2 border-navy/10 rounded-xl p-4 flex items-center justify-center bg-cream h-40">
                        <img
                          src={resolveAssetUrl(selectedApp.logo_url)}
                          alt={selectedApp.venue_name}
                          className="max-h-32 max-w-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-navy/20 rounded-xl p-4 flex items-center justify-center bg-cream h-40">
                        <div className="text-center">
                          <ImageIcon size={40} className="text-navy/20 mx-auto mb-2" />
                          <p className="text-sm text-navy/40">No logo uploaded</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Application Details */}
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {/* Venue Information */}
                    <div className="bg-white rounded-xl border border-navy/5 p-5" style={{ borderLeft: '4px solid #14b8a6' }}>
                      <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
                        <Building2 size={18} className="text-venue" />
                        Venue Information
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs text-navy/50 uppercase tracking-wide">Address</label>
                          <p className="font-medium text-navy">{selectedApp.street_address}</p>
                          <p className="text-sm text-navy/70">
                            {selectedApp.city}, {selectedApp.state} {selectedApp.zip_code}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-navy/50 uppercase tracking-wide">Phone</label>
                          <p className="font-medium text-navy flex items-center gap-2">
                            <Phone size={14} className="text-venue" />
                            {selectedApp.phone}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-navy/50 uppercase tracking-wide">Email</label>
                          <p className="font-medium text-navy flex items-center gap-2">
                            <Mail size={14} className="text-venue" />
                            {selectedApp.email}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Order Details */}
                    <div className="bg-white rounded-xl border border-navy/5 p-5" style={{ borderLeft: '4px solid #f97316' }}>
                      <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
                        <Package size={18} className="text-consumer" />
                        Order Details
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs text-navy/50 uppercase tracking-wide">Owner/Manager</label>
                          <p className="font-medium text-navy">{selectedApp.owner_manager_name}</p>
                        </div>
                        <div>
                          <label className="text-xs text-navy/50 uppercase tracking-wide">Average Daily Customers</label>
                          <p className="font-medium text-navy">{selectedApp.average_customers_per_day}</p>
                        </div>
                        <div>
                          <label className="text-xs text-navy/50 uppercase tracking-wide">Requested Coasters</label>
                          <p className="font-medium text-navy text-lg">{selectedApp.requested_coaster_quantity?.toLocaleString() || 0}</p>
                        </div>
                        <div>
                          <label className="text-xs text-navy/50 uppercase tracking-wide">Delivery Frequency</label>
                          <p className="font-medium text-navy capitalize">{selectedApp.coaster_delivery_frequency}</p>
                        </div>
                        <div>
                          <label className="text-xs text-navy/50 uppercase tracking-wide">Applied On</label>
                          <p className="font-medium text-navy">{formatDate(selectedApp.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Selected Design */}
                  {selectedApp.selected_design_id && (
                    <div className="mb-6">
                      <label className="block text-xs font-medium text-navy/50 uppercase tracking-wide mb-2">
                        Selected Coaster Design
                      </label>
                      <div className="border border-navy/10 rounded-xl p-4 bg-cream">
                        {coasterDesigns[selectedApp.selected_design_id] ? (
                          <div className="flex items-center gap-4">
                            <img
                              src={resolveAssetUrl(coasterDesigns[selectedApp.selected_design_id].image_url)}
                              alt={coasterDesigns[selectedApp.selected_design_id].name}
                              className="w-24 h-24 object-contain rounded-lg"
                            />
                            <div>
                              <p className="font-semibold text-navy">{coasterDesigns[selectedApp.selected_design_id].name}</p>
                              <p className="text-sm text-navy/50 capitalize">
                                {coasterDesigns[selectedApp.selected_design_id].season} {coasterDesigns[selectedApp.selected_design_id].year}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-navy/70 text-sm">Design ID: {selectedApp.selected_design_id}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Admin Message - Only show for pending applications */}
                  {selectedApp.status === 'pending' && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-navy/70 mb-2 flex items-center gap-2">
                        <MessageSquare size={16} className="text-business" />
                        Message to Applicant (Optional)
                      </label>
                      <textarea
                        value={reviewMessage}
                        onChange={(e) => setReviewMessage(e.target.value)}
                        placeholder="Add a personalized message that will be sent via email..."
                        rows={4}
                        className="w-full px-4 py-3 border-2 border-navy/10 rounded-xl focus:outline-none focus:border-business bg-white text-navy placeholder:text-navy/30"
                      />
                    </div>
                  )}

                  {/* Action Buttons - Only show for pending applications */}
                  {selectedApp.status === 'pending' ? (
                    <div className="flex gap-4">
                      <Button
                        onClick={() => handleReview(selectedApp.id, false)}
                        disabled={processing}
                        variant="outline"
                        className="flex-1 border-2 border-red-200 text-red-500 hover:bg-red-50"
                      >
                        {processing ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-red-500 border-t-transparent mr-2" />
                        ) : (
                          <X className="mr-2" size={18} />
                        )}
                        Reject
                      </Button>
                      <Button
                        onClick={() => handleReview(selectedApp.id, true)}
                        disabled={processing}
                        className="flex-1"
                        style={{
                          background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                          color: 'white'
                        }}
                      >
                        {processing ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                        ) : (
                          <CheckCircle className="mr-2" size={18} />
                        )}
                        Approve & Create Hub
                      </Button>
                    </div>
                  ) : (
                    <div className="flex justify-end">
                      <Button
                        onClick={() => setSelectedApp(null)}
                        variant="outline"
                        className="border-navy/10 text-navy/70"
                      >
                        Close
                      </Button>
                    </div>
                  )}
                </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default VenueApplications;
